import {
    Button,
    Form,
    type FormProps,
    Input,
    Modal,
    Space,
    type NotificationArgsProps,
    notification,
    Checkbox,
    Select
} from 'antd'
import React, { useEffect, useState } from 'react'

import { CreatorService } from '@services/CreatorService'
import { type ProductParams, ProductService } from '@services/ProductService'

import type { Creator, Product } from '../../types'

type ModalProps = {
    product: Product
    isOpen: boolean
    onClose: () => void
    refetch: () => Promise<void>
}

type FieldType = {
    name?: string
    slug?: string
    label?: string
    key?: string
    description?: string
    isPublic: boolean
    creatorId?: string
}

type NotificationPlacement = NotificationArgsProps['placement']
type NotificationType = 'success' | 'error'

export const EditProductModal: React.FC<ModalProps> = ({ isOpen, product, onClose, refetch }) => {
    const [api, contextHolder] = notification.useNotification()
    const [isLoading, setIsLoading] = useState(false)
    const [creators, setCreators] = useState<Creator[]>()
    const [form] = Form.useForm()

    const openNotification = (
        placement: NotificationPlacement,
        type: NotificationType,
        message: string,
        description?: string
    ) => {
        api[type]({
            message,
            description,
            placement
        })
    }

    useEffect(() => {
        const fetchCreators = async () => {
            const creators = await CreatorService.list()
            setCreators(creators)
        }
        fetchCreators()
    }, [])

    const onFinish: FormProps<FieldType>['onFinish'] = async values => {
        const body: ProductParams = {
            name: values.name!,
            slug: values.slug!,
            label: values.label!,
            key: values.key!,
            description: values.description,
            isPublic: values.isPublic,
            creatorId: values.creatorId
        }

        try {
            setIsLoading(true)
            await ProductService.update(product.id!, body)
            openNotification('top', 'success', 'Product updated!')
            await refetch()
            onClose()
            form.resetFields()
        } catch (err: any) {
            openNotification('top', 'error', 'Product update failed!', err.toString())
        } finally {
            setIsLoading(false)
        }
    }

    if (!product || !creators) {
        return null
    }

    return (
        <Modal open={isOpen} footer={[]} onCancel={onClose}>
            {contextHolder}
            <Form
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 14 }}
                layout="horizontal"
                disabled={false}
                onFinish={onFinish}
            >
                <h3>Edit Product</h3>
                <Form.Item label="Product ID">
                    <span>{product.id}</span>
                </Form.Item>
                <Form.Item
                    label="Name"
                    name="name"
                    initialValue={product.name}
                    rules={[{ required: true, message: 'Please enter an internal product name' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    label="Slug"
                    name="slug"
                    initialValue={product.slug}
                    rules={[{ required: true, message: 'Please enter an internal product name' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    label="Label"
                    name="label"
                    initialValue={product.label}
                    rules={[{ required: true, message: 'Please enter a product label' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    label="Key"
                    name="key"
                    initialValue={product.key}
                    rules={[{ required: true, message: 'Please enter a product key' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item label="Description" name="description">
                    <Input />
                </Form.Item>
                <Form.Item label="Creator" name="creatorId" initialValue={product.creatorId}>
                    <Select
                        style={{ minWidth: '200px' }}
                        options={creators?.map(creator => ({
                            label: creator.name,
                            value: creator.id
                        }))}
                    />
                </Form.Item>
                <Form.Item label="Is Public" name="isPublic" valuePropName="checked" initialValue={product.isPublic}>
                    <Checkbox />
                </Form.Item>
                <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={isLoading}>
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    )
}
